<script setup lang="ts">
import type { SearchCriterions } from '~/models/Search'
import type { SearchResponse } from '~/models/Content/Response'
import type { ContentProduct } from '~/models/Content/ContentProduct'
import { useI18n } from 'vue-i18n'
import { computed, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { useRouter } from '#imports'
import { setTitle } from '~/utils/dom'
import useAuthStore from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import useSearchParams from '~/composables/useSearchParams'
import useSearchHelper from '~/composables/useSearchHelper'
import useSearchClient from '~/composables/useSearchClient'
import { useScrollBehavior } from '~/composables/useScrollBehavior'
import useContentMapper from '~/composables/useContentMapper'
import { useAppHeaderPosition } from '~/composables/useAppHeaderPosition'
import { useAppColor } from '~/composables/useAppColor'
import AuPagination from '~/components/ui/AuPagination.vue'
import SearchInput from '~/components/search/SearchInput.vue'
import SearchActiveFilters from '~/components/search/SearchActiveFilters.vue'
import NoSearchResults from '~/components/search/NoSearchResults.vue'
import LibrarySearchResults from '~/components/products/library/LibrarySearchResults.vue'
import LibrarySearchRelevantCards from '~/components/products/library/LibrarySearchRelevantCards.vue'
import LibrarySearchFilters from '~/components/products/library/LibrarySearchFilters.vue'

const props = defineProps<{ product: ContentProduct }>()

const { t } = useI18n()
const router = useRouter()
const { mapResult, mapContents } = useContentMapper()
const { searchPath, cardFields } = useSearchHelper()
const { isAuthenticated, userGradesByGradeType } = storeToRefs(useAuthStore())

const {
  searchQuery,
  page,
  queryParams,
  gradeCriterion,
  languageCriterion,
  themeCriterion,
  seriesCriterion,
  genreCriterion,
  checkboxCriterion,
  hasCriterions,
  hasQuery,
  sort,
  setSortField,
  toggleCriterion,
  resetCriterions,
  resetSearch,
} = useSearchParams({ product: props.product })

if (!gradeCriterion.value.length && userGradesByGradeType.value.length) {
  gradeCriterion.value = userGradesByGradeType.value
}

const { fetchResults, results: response } = useSearchClient<SearchResponse>(
  searchPath.value,
  { transformData: (response: SearchResponse) => response }
)

useScrollBehavior()
useAppColor({ themeColor: ColorName.Purple50, bgColor: ColorName.Gray5 })
useAppHeaderPosition('relative')

const FUZZINESS = 1.0
const PAGE_LIMIT = 24
const AGGREGATION_FIELDS = ['grades', 'languages', 'themes', 'series', 'genres', 'has_audio', 'has_more']
const BOOST_FIELDS = { search_keywords: 20, title: 10, m_intro: 4, intro: 2 }

const criterions = computed((): SearchCriterions => ({
  language: languageCriterion.value,
  theme: themeCriterion.value,
  series: seriesCriterion.value,
  genre: genreCriterion.value,
  checkbox: checkboxCriterion.value,
  grade: gradeCriterion.value,
}))

const results = computed(() => data.value ? mapContents(data.value) : [])
const totalCount = computed(() => data.value?.count || 0)
const totalPages = computed(() => Math.ceil(totalCount.value / PAGE_LIMIT))
const isLoading = computed(() => isLoadingResults.value || isFetching.value)
const offset = computed(() => (page.value - 1) * PAGE_LIMIT)
const queryKey = computed(() => ['product-search', props.product.pathString, searchQuery.value, page.value, criterions.value, sort.value])
const showRelevantCards = computed(() => page.value === 1)
const subtreeCriterion = computed(() => [props.product.pathString])

const spellingIncorrect = computed(() => !!response.value?.Spellcheck?.Incorrect)
const spellingSuggestion = computed(() => response.value?.Spellcheck?.Suggestion ?? '')

const doLibrarySearch = async ({ signal }: { signal?: AbortSignal }) => {
  return await fetchResults({
    text: searchQuery.value,
    fuzziness: FUZZINESS,
    spellcheck: true,
    fields: cardFields,
    boost: BOOST_FIELDS,
  }, {
    contentTypeCriterion: [ContentType.BookReader],
    subtreeCriterion: subtreeCriterion.value,
    languageFieldCriterion: languageCriterion.value,
    gradeFieldCriterion: gradeCriterion.value,
    seriesFieldCriterion: seriesCriterion.value,
    themeFieldCriterion: themeCriterion.value,
    genreFieldCriterion: genreCriterion.value,
    checkboxFieldCriterion: checkboxCriterion.value,
    sortField: sort.value.field,
    sortOrder: sort.value.order,
    gradeOperator: 'or',
    aggregationCriterion: AGGREGATION_FIELDS,
    mainLocationCriterion: true,
  }, PAGE_LIMIT, offset.value, signal)
}

const {
  data,
  isError,
  isLoading: isLoadingResults,
  isFetching,
} = useQuery({
  queryKey,
  queryFn: doLibrarySearch,
  select: data => mapResult(data),
  staleTime: Infinity,
  enabled: computed(() => isAuthenticated.value && !!props.product.pathString),
  placeholderData: previousData => previousData,
})

const replaceRoute = () =>
  router.replace({ name: 'product', query: queryParams.value })

const updateSearchQuery = (newQuery: string) => {
  searchQuery.value = newQuery
  page.value = 1
}

const pushPage = (newPage: number) => {
  page.value = newPage
  scrollTo(0, 0)
}

watchEffect(() => setTitle(props.product.title || ''))
watch(queryKey, () => replaceRoute(), { deep: true })
watch(userGradesByGradeType, () => gradeCriterion.value = userGradesByGradeType.value)
</script>

<template>
  <div class="max-w-screen-au grid-areas-library-view-mobile md:grid-areas-library-view-tablet mx-auto my-12 grid grid-cols-1 grid-rows-[auto_auto_auto_1fr_auto] gap-x-8 gap-y-4 px-4 sm:px-8 md:grid-cols-3 md:gap-x-12">
    <SearchInput
      :title="product.title"
      :placeholder="t(`search.product.placeholder.${product.title}`)"
      :query-key="queryKey"
      :search-query="searchQuery"
      @update-search-query="updateSearchQuery"
    />
    <LibrarySearchFilters
      v-if="data"
      :results="data"
      :is-loading="isLoadingResults"
      :is-refetching="isLoading"
      :criterions="criterions"
      @toggle-criterion="toggleCriterion"
    />
    <SearchActiveFilters
      :criterions="criterions"
      :is-loading="isLoading"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :field="searchQuery.length ? sort.field : undefined"
      group-grades-by-age
      @remove-criterion="toggleCriterion"
      @reset-criterions="resetCriterions"
      @set-sort-field="setSortField"
    />
    <LibrarySearchRelevantCards
      v-if="showRelevantCards"
      :grades="gradeCriterion"
      :path-string="product.pathString"
    />
    <div class="grid-in-results">
      <LibrarySearchResults
        :results="results"
        :is-loading="isLoading"
        :product-title="product.title"
      />
      <NoSearchResults
        :no-results="!results.length"
        :has-query="hasQuery"
        :has-failed="isError"
        :is-loading="isLoading"
        :has-criterions="hasCriterions"
        :spelling-suggestion="spellingIncorrect ? spellingSuggestion : ''"
        @reset-criterions="resetCriterions"
        @reset-search="resetSearch"
        @suggested-search="updateSearchQuery(spellingSuggestion)"
      />
    </div>
    <AuPagination
      v-if="totalCount > PAGE_LIMIT"
      :page
      button-color="purple"
      :disabled="isLoading"
      :total="totalCount"
      :items-per-page="PAGE_LIMIT"
      class="grid-in-pagination mt-12 ml-auto"
      @update:page="pushPage"
    />
  </div>
</template>
